import { useContext, useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { getErrorMessage } from "src/lib/utils";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useModalAction } from "src/components/modal-views/context";
import { useLocation, useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;

interface KafkaNotification {
  id: number;
  card_id: string;
  user_id: string;
  auth_code: string;
  card_number_masked: string;
  event_type: string;
  event_type_name: string;
  event_code: string;
  amount: string;
  to_source_amount:string;
  status: string;
  status_reason: string;
  response_code: number;
  merchant_name: string;
  merchant_city: string;
  merchant_number: string;
  trace_number: number;
  operation_time: string;
  is_reversal: boolean;
}

interface KafkaResponse {
  data: KafkaNotification[];
  pagination: {
    total_records: number;
    total_pages: number;
    current_page: number;
    per_page: number;
  };
}


const VirtualCardKafkaNotifications = () => {
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList } = usePermissions(
    permissionConstants.CARD_KAFKA_NOTIFICATION
  );
  const canViewNotifications = canView || canList;

  const [tableData, setTableData] = useState<KafkaNotification[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: LIST_PAGE_SIZE,
    total: 0,
  });
  const location = useLocation();

  const [searchType, setSearchType] = useState<"card_id" | "last4" | "user_id">(
    "card_id",
  );
  const pathCurrent = location.pathname.split("/")[1];

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);
  const { openModal, closeModal } = useModalAction();
  const navigate = useNavigate();

 const onSelectUser = (userID: number) => {
    navigate(`/users/${String(userID)}`, {
      replace: true,
    });
  };
  const resetPage = () =>
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));

  const { mutate: getCardUserDetails } = useMutation(
    client.userVirtualCards.getUserDetails,
    {
      onSuccess: (res: any) => {
        openModal("CARD_USER_DETAILS", {
          data: res?.data?.[0],
        });
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const columns: ColumnsType<KafkaNotification> = useMemo(
    () => [
      {
        title: "Card ID",
        dataIndex: "card_id",
        render: (val: string, record: any) => (
          <span
            className="text-blue-500 cursor-pointer underline"
            onClick={() => {
              getCardUserDetails({
                id: record.card_id,
              });
            }}
          >
            {val}
          </span>
        ),
      },
      {
        title: "User ID",
        dataIndex: "user_id",
        render: (value) => (
          <div className="flex flex-row gap-2 items-center">
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline"
              onClick={() => onSelectUser(value)}
            >
              {value}
            </a>
          </div>
        ),
      },
      {
        title: "Auth Code",
        dataIndex: "auth_code",
      },
      {
        title: "Masked Card",
        dataIndex: "card_number_masked",
      },
      // {
      //   title: "Event Type",
      //   dataIndex: "event_type",
      // },
      {
        title: "Event Name",
        dataIndex: "event_type_name",
      },
      // {
      //   title: "Event Code",
      //   dataIndex: "event_code",
      // },
      {
        title: "Amount",
        dataIndex: "amount",
      },
      {
        title: "Source Amount",
        dataIndex: "to_source_amount",
      },
      {
        title: "Status",
        dataIndex: "status",
      },
      {
        title: "Status Reason",
        dataIndex: "status_reason",
      },
      //   {
      //     title: "Response Code",
      //     dataIndex: "response_code",
      //   },
      {
        title: "Merchant Name",
        dataIndex: "merchant_name",
      },
      {
        title: "Merchant City",
        dataIndex: "merchant_city",
      },
      {
        title: "Merchant Number",
        dataIndex: "merchant_number",
      },
      {
        title: "Trace Number",
        dataIndex: "trace_number",
      },
      {
        title: "Operation Time",
        render: (_, record) => new Date(record.operation_time).toLocaleString(),
      },
      {
        title: "Reversal",
        render: (_, record) => (record.is_reversal ? "Yes" : "No"),
      },
    ],
    [],
  );

  const { mutate: getKafkaNotificationsList, isLoading } = useMutation(
    client.cardManagementOtherApis.getKafkaNotificationsList,
    {
      onSuccess: (res: any) => {
        setTableData(res?.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.total_records || 0,
        }));
      },
      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    },
  );

  /* debounce */

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      resetPage();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    setSearchValue("");
    setDebouncedSearchValue("");
    resetPage();
  }, [searchType]);

  useEffect(() => {
    if (!isMounted || !canViewNotifications) return;

    const payload: any = {
      page: pagination.page,
      limit: pagination.limit,
    };

    if (debouncedSearchValue.trim()) {
      payload[searchType] = debouncedSearchValue.trim();
    }

    if (dateRange) {
      payload.from_date = dateRange[0].format("YYYY-MM-DD");
      payload.to_date = dateRange[1].format("YYYY-MM-DD");
    }

    getKafkaNotificationsList(payload);
  }, [
    isMounted,
    canViewNotifications,
    pagination.page,
    debouncedSearchValue,
    searchType,
    dateRange,
  ]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="mb-5 text-2xl font-bold">Kafka Notifications History</h1>

        <div className="flex flex-wrap items-center gap-4">
          {/* Search Type */}
          <select
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value as "card_id" | "last4" | "user_id");
              setSearchValue("");
              setPagination((prev) => ({
                ...prev,
                page: 1,
              }));
            }}
            className="h-11 w-[220px] rounded-lg border border-gray-300 px-4 outline-none"
          >
            <option value="card_id">Card ID</option>
            <option value="last4">Card Last 4</option>
            <option value="user_id">User ID</option>
          </select>

          {/* Search Value */}
          <input
            value={searchValue}
            onChange={(e) => {
              const value =
                searchType === "last4"
                  ? e.target.value.replace(/\D/g, "").slice(0, 4)
                  : e.target.value;

              setSearchValue(value);
            }}
            type={searchType === "user_id" ? "number" : "text"}
            placeholder={
              searchType === "card_id"
                ? "Enter Card ID"
                : searchType === "last4"
                  ? "Enter Last 4 Digits"
                  : "Enter User ID"
            }
            maxLength={searchType === "last4" ? 4 : undefined}
            className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
          />

          {/* Date Filter */}
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            className="!h-11"
          />
        </div>
      </div>
      {!canViewNotifications ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view kafka notifications.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={tableData}
            loading={isLoading}
            scroll={{
              x: 1800,
            }}
            pagination={{
              current: pagination.page,
              total: pagination.total,
              pageSize: pagination.limit,
              showSizeChanger: false,
              position: ["bottomCenter"],
              onChange: (page) =>
                setPagination((prev) => ({
                  ...prev,
                  page,
                })),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VirtualCardKafkaNotifications;
